import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, GraduationCap, Star, UserPlus } from "lucide-react";
import { Avatar } from "@/components/site/AvatarUpload";
import { api } from "@/lib/api";
import { useSeo, pageSeo } from "@/lib/seo";

const SEO = pageSeo("/graduates");

export const Stars = ({ n, className = "h-4 w-4" }) => (
  <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${className} ${i <= n ? "fill-[#F08A3C] text-[#F08A3C]" : "text-slate-300"}`} />)}
  </span>
);

function GraduateCard({ g }) {
  return (
    <article className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6 flex flex-col gap-4" data-testid="graduate-card">
      <div className="flex items-center gap-3">
        <Avatar user={g} className="h-12 w-12 text-sm" />
        <div><p className="font-semibold text-[#0B2A4A]" data-testid="graduate-name">{g.name}</p><p className="text-xs text-slate-500">{g.course}</p></div>
      </div>
      <Stars n={g.rating} />
      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap flex-1">"{g.body}"</p>
      <Link to={`/verify/${g.certificate_id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#147C88]" data-testid="graduate-verify-link"><BadgeCheck className="h-4 w-4" /> Verified graduate · view certificate</Link>
    </article>
  );
}

function FirstGraduate() {
  return (
    <div className="rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-8 sm:p-14 text-center flex flex-col items-center gap-4" data-testid="graduates-empty">
      <GraduationCap className="h-12 w-12 text-[#F08A3C]" />
      <h2 className="text-2xl sm:text-3xl font-bold text-white">Be our first graduate</h2>
      <p className="max-w-xl text-[#F5ECD7]/80">Our first class is enrolling now. Complete your course, earn your certificate and your story will be featured here.</p>
      <Link to="/signup" className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold hover:bg-[#D96F28]" data-testid="graduates-register-btn"><UserPlus className="h-4 w-4" /> Register for a course</Link>
    </div>
  );
}

export default function GraduatesPage() {
  useSeo(SEO);
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/graduates").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 flex flex-col gap-10" data-testid="graduates-page">
      <div className="max-w-2xl">
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">GRADUATES</p>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B2A4A]">Our graduates</h1>
        <p className="mt-4 text-slate-600">Real reviews from students who completed a Digital Nomad Nicaragua course and earned a verified certificate.</p>
      </div>
      {list && (list.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{list.map((g) => <GraduateCard key={g.id} g={g} />)}</div> : <FirstGraduate />)}
      {list?.length > 0 && <Link to="/signup" className="self-start inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold" data-testid="graduates-register-btn">Register for a course</Link>}
    </section>
  );
}
